import { Auth } from 'aws-amplify';
import appConfig from '../config/app-config';
import type { CreateLegalCasePayload } from '../types/LegalCase';

const API = appConfig.apis.recoveryApiEnv;
const baseUrl = API.address;

export class ApiError extends Error {
    constructor(public status: number, message: string, public data?: any) {
        super(message);
    }
}

const getHeaders = async (): Promise<Record<string, string>> => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    return {
        'Content-Type': 'application/json',
        'x-api-key': API.key,
        'Authorization': `Bearer ${token}`,
    };
};

const handleResponse = async (res: Response) => {
    if (res.ok) return res.json();
    const data = await res.json().catch(() => null);
    throw new ApiError(res.status, data?.message || `API error: ${res.status}`, data);
};

const get = async (path: string) =>
    handleResponse(await fetch(`${baseUrl}${path}`, { headers: await getHeaders() }));

const post = async (path: string, data: Record<string, unknown>) =>
    handleResponse(await fetch(`${baseUrl}${path}`, {
        method: 'POST',
        headers: await getHeaders(),
        body: JSON.stringify(data),
    }));

const put = async (path: string, data: Record<string, unknown>) =>
    handleResponse(await fetch(`${baseUrl}${path}`, {
        method: 'PUT',
        headers: await getHeaders(),
        body: JSON.stringify(data),
    }));

// Convenção de query param do recovery: page/size/sort (não pageSize/sortField).
const qs = (params: Record<string, unknown>) => {
    const s = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v != null && v !== '') s.set(k, String(v)); });
    const str = s.toString();
    return str ? `?${str}` : '';
};

export const recoveryApi = {
    getLegalCases: (params?: {
        page?: number; size?: number; sort?: string;
        search?: string; pole?: string; type?: string; status?: string;
    }) => get(`/legal-cases/page-list${qs(params || {})}`),

    getLegalCaseView: (uuid: string) => get(`/legal-cases/${uuid}/page-view`),

    getLegalCaseFormOptions: () => get(`/legal-cases/page-form`),

    createLegalCase: (data: CreateLegalCasePayload) => post(`/legal-cases`, data as unknown as Record<string, unknown>),

    updateLegalCase: (uuid: string, data: CreateLegalCasePayload) =>
        put(`/legal-cases/${uuid}`, data as unknown as Record<string, unknown>),
};
